import { type ComponentPropsWithoutRef, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { type FiltersTrackingLocation, useTrackFiltersApplied, useTrackFiltersRemoved } from '@air/analytics';
import { cn } from '@air/utils-tailwind';

import { ColorFilterTrigger } from '~/components/Filters/FiltersBar/ColorFilterTrigger';
import { CreatedDateFilterTrigger } from '~/components/Filters/FiltersBar/CreatedDateFilterTrigger';
import { CustomFieldFilterTrigger } from '~/components/Filters/FiltersBar/CustomFieldFilterTrigger';
import { ui2026FiltersBar } from '~/components/Filters/FiltersBar/filtersBarUi2026';
import { ModifiedDateFilterTrigger } from '~/components/Filters/FiltersBar/ModifiedDateFilterTrigger';
import { OrientationFilterTrigger } from '~/components/Filters/FiltersBar/OrientationFilterTrigger';
import { OtherFiltersTrigger } from '~/components/Filters/FiltersBar/OtherFiltersTrigger';
import { SmartTagFilterTrigger } from '~/components/Filters/FiltersBar/SmartTagFilterTrigger';
import { TagFilterTrigger } from '~/components/Filters/FiltersBar/TagFilterTrigger';
import { TypeFilterTrigger } from '~/components/Filters/FiltersBar/TypeFilterTrigger';
import { UploadedDateFilterTrigger } from '~/components/Filters/FiltersBar/UploadedDateFilterTrigger';
import { UploaderFilterTrigger } from '~/components/Filters/FiltersBar/UploaderFilterTrigger';
import { defaultOtherFilters } from '~/components/Filters/OtherFilters/OtherFilters';
import { type ItemTypeFilter, type SingleFilterProps } from '~/components/Filters/types';
import { PublicMoreFiltersTrigger } from '~/components/Filters/v2/components/PublicMoreFiltersTrigger';
import { usePublicFastFilterOptions } from '~/components/Filters/v2/hooks/usePublicFastFilterOptions';
import { manuallyHideDropdown } from '~/components/Menus/DropdownMenuWithActionSheet';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { useApplyFilters } from '~/hooks/filters/useApplyFilters';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { filtersValuesSelector } from '~/store/filters/selectors';
import { type SelectedFilter } from '~/store/filters/types';
import { filterTypeToTrackingType } from '~/store/filters/utils';
import { useGetPublicFilterOptions } from '~/swr-hooks/filters/useGetPublicFilterOptions';
import { filterParamsToTrackingType, getUrlParamsFromFilters } from '~/utils/filters/filtersToUrlParamsUtils';
import {
  canSeeCustomFields,
  canSeeWorkspaceSmartTags,
  canSeeWorkspaceTags,
} from '~/utils/permissions/workspacePermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export type FiltersBarProps = Pick<ComponentPropsWithoutRef<'div'>, 'className'> & {
  trackLocation: FiltersTrackingLocation;
};

const availableFilterTypes: ItemTypeFilter[] = ['assets', 'files', 'boards'];

export const PublicFiltersBar = memo(({ className, trackLocation }: FiltersBarProps) => {
  const {
    filtersInBar,
    filtersInDropdown,
    customFieldsInDropdown,
    otherFilters = defaultOtherFilters,
    disabledFilters,
  } = usePublicFastFilterOptions();
  const board = useSelector(centralizedBoardSelector);
  const { data: filtersOptions } = useGetPublicFilterOptions({ boardId: board?.id ?? '' });

  const store = useAirStore();
  const { applyFilters, removeFilters } = useApplyFilters();
  const { trackFiltersRemoved } = useTrackFiltersRemoved();
  const { trackFiltersApplied } = useTrackFiltersApplied();
  const { permissions } = usePublicPermissions();

  const canViewCustomFields = canSeeCustomFields(permissions);
  const canSeeTags = canSeeWorkspaceTags(permissions);
  const canSeeSmartTags = canSeeWorkspaceSmartTags(permissions);

  const renderFilterTrigger = useCallback(
    (filter: SelectedFilter) => {
      const onApplyClick = async () => {
        const filters = filtersValuesSelector(store.getState());
        await applyFilters({ filters });
        manuallyHideDropdown();

        const appliedFilters = getUrlParamsFromFilters(filters);
        trackFiltersApplied({
          types: filterParamsToTrackingType(appliedFilters),
          location: trackLocation,
          builderType: 'pinned-filters',
        });
      };

      const onRemoveClick = async (filters: SelectedFilter[]) => {
        await removeFilters({ filters });
        trackFiltersRemoved({
          types: filterTypeToTrackingType(filter.type),
          location: trackLocation,
          builderType: 'pinned-filters',
        });
      };

      const commonProps: SingleFilterProps = {
        onApplyClick,
        onRemoveClick,
      };

      switch (filter.type) {
        case 'tags':
          return canSeeTags && <TagFilterTrigger key={filter.type} {...commonProps} />;
        case 'smartTags':
          return canSeeSmartTags && <SmartTagFilterTrigger key={filter.type} {...commonProps} />;
        case 'color':
          return <ColorFilterTrigger key={filter.type} colors={filtersOptions?.colors ?? []} {...commonProps} />;
        case 'customField':
          return filter.customField && canViewCustomFields ? (
            <CustomFieldFilterTrigger key={filter.customField.id} customField={filter.customField} {...commonProps} />
          ) : null;
        case 'dateCreated':
          return <CreatedDateFilterTrigger key={filter.type} {...commonProps} />;
        case 'dateModified':
          return <ModifiedDateFilterTrigger key={filter.type} {...commonProps} />;
        case 'dateUploaded':
          return <UploadedDateFilterTrigger key={filter.type} {...commonProps} />;
        case 'other':
          return <OtherFiltersTrigger otherFilters={otherFilters} key={filter.type} {...commonProps} />;
        case 'type':
          return <TypeFilterTrigger key={filter.type} availableTypes={availableFilterTypes} {...commonProps} />;
        case 'uploader':
          return (
            <UploaderFilterTrigger uploaders={filtersOptions?.uploaders ?? []} key={filter.type} {...commonProps} />
          );
        case 'orientation':
          return <OrientationFilterTrigger key={filter.type} {...commonProps} />;
        case 'separator1':
          return <div key={filter.type} className="h-6 w-px ads:bg-ads-gray-4 bg-gray-4" aria-hidden="true" />;
      }
    },
    [
      store,
      applyFilters,
      trackFiltersApplied,
      trackLocation,
      removeFilters,
      trackFiltersRemoved,
      canSeeTags,
      canSeeSmartTags,
      filtersOptions?.colors,
      filtersOptions?.uploaders,
      canViewCustomFields,
      otherFilters,
    ],
  );

  return (
    <div className={cn('flex items-center gap-2 overflow-auto px-3 pt-2 pb-3', ui2026FiltersBar(), className)}>
      {filtersInBar.map((filter) => renderFilterTrigger(filter))}
      <PublicMoreFiltersTrigger
        trackLocation={trackLocation}
        otherFilters={otherFilters}
        availableTypes={availableFilterTypes}
        filters={filtersInDropdown}
        customFields={customFieldsInDropdown}
        disabledFilters={disabledFilters}
      />
    </div>
  );
});

PublicFiltersBar.displayName = 'PublicFiltersBar';
